// Phone and email validation shared by the loyalty flows.
//
// Three components used to validate phone numbers three different ways —
// LoyaltyDetailsModal with an NG/GH regex, LoadMemberBenefits with a hardcoded
// `length !== 11` (which rejected every Ghanaian number), and RegisterMember
// with a loose 10-15 digit E.164 test. This module is the single source.
//
// The same number has three written forms. The trunk `0` and the dial code are
// alternatives — you use one or the other, never both:
//   local     0 + national number        08012345678
//   plain     dial code + national       2348012345678
//   E.164     + dial code + national     +2348012345678
//
// Where a country selector is on screen it already carries the dial code, so
// the field beside it holds the NATIONAL number alone (8012345678) and the
// operator never types the leading 0. Use toNsn/isValidNsn/composePhone for
// those fields. The whole-string helpers (sanitizePhone/isValidPhone) remain
// for the places with no selector, where any of the three forms may be typed.

// `nsnPrefix` is the set of mobile network prefixes the national number may
// start with. Both countries use a `0` trunk code and Ghana's numbers are one
// digit shorter, so without this a truncated Nigerian number (0801234567)
// validates as a well-formed Ghanaian one. The prefixes disambiguate them.
export const PHONE_COUNTRIES = {
  NG: {
    code: "NG",
    name: "Nigeria",
    dialCode: "234",
    // National significant number: what follows the trunk 0 or the dial code.
    nsnLength: 10,
    nsnPrefix: "70|71|80|81|90|91",
    example: "8012345678",
  },
  GH: {
    code: "GH",
    name: "Ghana",
    dialCode: "233",
    nsnLength: 9,
    nsnPrefix: "2|5",
    example: "241234567",
  },
};

export const DEFAULT_PHONE_COUNTRY = "NG";

const getCountry = (countryCode) =>
  PHONE_COUNTRIES[countryCode] || PHONE_COUNTRIES[DEFAULT_PHONE_COUNTRY];

/**
 * Longest string still worth accepting, given what has been typed so far.
 * The prefix decides the form, so a number starting `0` caps tighter than one
 * starting `+`. This is what stops the field accepting digits forever.
 */
export const maxPhoneLength = (value, countryCode) => {
  const { dialCode, nsnLength } = getCountry(countryCode);
  const raw = String(value || "");

  if (raw.startsWith("+")) return 1 + dialCode.length + nsnLength;
  if (raw.startsWith(dialCode)) return dialCode.length + nsnLength;
  if (raw.startsWith("0")) return 1 + nsnLength;

  // Nothing recognisable typed yet — allow the longest valid form.
  return 1 + dialCode.length + nsnLength;
};

/**
 * Strip anything that cannot appear in a phone number and truncate to the
 * longest form the country allows. A `+` survives only in first position.
 */
export const sanitizePhone = (value, countryCode) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  const candidate = (hasPlus ? "+" : "") + digits;

  return candidate.slice(0, maxPhoneLength(candidate, countryCode));
};

export const isValidPhone = (value, countryCode) => {
  const { dialCode, nsnLength, nsnPrefix } = getCountry(countryCode);
  const raw = String(value || "").trim();

  const prefix = `(?:${nsnPrefix})`;
  // Remaining digits after the network prefix. The prefix alternatives share a
  // length within each country, so measuring the first one is enough.
  const rest = nsnLength - nsnPrefix.split("|")[0].length;

  return new RegExp(
    `^(?:\\+?${dialCode}|0)${prefix}\\d{${rest}}$`
  ).test(raw);
};

/**
 * Reduce anything the operator types or pastes to the national number alone.
 * Tolerates the redundant forms so a pasted `+2348012345678`, `2348012345678`
 * or `08012345678` all collapse to `8012345678`.
 */
export const toNsn = (value, countryCode) => {
  const { dialCode, nsnLength } = getCountry(countryCode);
  let digits = String(value || "").replace(/\D/g, "");

  // Only strip the dial code when what remains could still be a full number —
  // otherwise a Ghanaian number legitimately starting "233" would lose it.
  if (digits.startsWith(dialCode) && digits.length > nsnLength) {
    digits = digits.slice(dialCode.length);
  }

  // A leading 0 is the trunk code, which the selector already replaces.
  if (digits.startsWith("0")) digits = digits.slice(1);

  return digits.slice(0, nsnLength);
};

/** Is this a complete, well-formed national number for the country? */
export const isValidNsn = (nsn, countryCode) => {
  const { nsnLength, nsnPrefix } = getCountry(countryCode);
  const rest = nsnLength - nsnPrefix.split("|")[0].length;

  return new RegExp(`^(?:${nsnPrefix})\\d{${rest}}$`).test(
    String(nsn || "").trim()
  );
};

/**
 * Build the number to send to the API from the selector + national number.
 * `local` (0 + national) is the format the POS has always sent, so it stays the
 * default; `e164` is available for callers that need the dial code.
 */
export const composePhone = (nsn, countryCode, format = "local") => {
  const { dialCode } = getCountry(countryCode);
  const digits = String(nsn || "").replace(/\D/g, "");
  if (!digits) return "";

  return format === "e164" ? `+${dialCode}${digits}` : `0${digits}`;
};

/** Which of the supported countries a number looks like, or null. */
export const detectPhoneCountry = (value) => {
  const raw = String(value || "").trim().replace(/^\+/, "");

  const match = Object.values(PHONE_COUNTRIES).find(
    (country) =>
      raw.startsWith(country.dialCode) &&
      raw.length === country.dialCode.length + country.nsnLength
  );

  return match ? match.code : null;
};

// yup's .email() accepts "a@b", which is not a deliverable address. Require a
// dotted TLD of at least two letters, well-formed domain labels (so "a@b..com"
// is rejected), and no whitespace on either side of the @.
const EMAIL_PATTERN =
  /^[^\s@]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export const isValidEmail = (value) => EMAIL_PATTERN.test(String(value || "").trim());

export const sanitizeEmail = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "");
